<template>
  <Dropdown
    :disabled
    :shown
    :placement
    :triggers="[]"
    :auto-hide="false"
    auto-boundary-max-size
  >
    <ToggleButton
      v-bind="$attrs"
      :count
      :disabled
      :unlabeled
      @click="handleToggle"
      @click:clear="handleClear"
    />
    <template #popper>
      <div
        ref="containerRef"
        class="ui-filter"
        :data-ref="containerRefId"
      >
        <div class="ui-filter__header">
          <div class="ui-filter__header-title">
            {{ t('Active filters') }}
          </div>
          <SavedFilters
            v-if="filterKey"
            v-model="modelStorage"
            :filter-key
          />
          <UIButtonIcon
            size="xs"
            variant="gray"
            fill="light"
            data-refid="filter__close-popup_button"
            :leading="XMarkIcon"
            class="ui-filter__header-button"
            @click="handleToggle"
          />
        </div>
        <Groups
          v-model="storage"
          :fields
          :flat
        />
      </div>
    </template>
  </Dropdown>
</template>

<script setup lang="ts">
import { computed, provide, ref, useTemplateRef, watch } from 'vue'
import { isEqual } from 'lodash'
import { onClickOutside } from '@vueuse/core'
import { Dropdown, Placement } from 'floating-vue'

import { Filter, FilterField, FilterGroup } from './utils/types'

import { useLocale } from '@/plugins/i18n'
import { useHotKeys } from '@/plugins/hotKeys'

import { formatFilterParams, getActiveFilters, normalizeOldData } from './utils/helpers'
import { generateRandomKey } from './components/utils/helpers'

import { XMarkIcon } from '@heroicons/vue/24/outline'
import { UIButtonIcon } from '@ui/buttons'
import Groups from './components/Groups.vue'
import ToggleButton from './components/ToggleButton.vue'
import SavedFilters from './SavedFilters.vue'

type Props = {
  disabled?: boolean
  fields?: FilterField[]
  flat?: boolean
  placement?: Placement
  unlabeled?: boolean
  filterKey?: string
}

const { flat, fields, placement = 'bottom-end' } = defineProps<Props>()

const modelValue = defineModel<Filter | FilterGroup>()
const modelStorage = defineModel<Filter>('storage')

defineOptions({
  name: 'UIFilter',
  inheritAttrs: false,
})

const { t } = useLocale('components.UI.Filter')
provide('t', t)

const containerRef = useTemplateRef('containerRef')

const shown = ref(false)

const containerRefId = `filter_${generateRandomKey()}`
provide('container', `[data-ref="${containerRefId}"]`)

const storage = computed<Filter>({
  get() {
    return normalizeOldData(modelStorage.value)
  },
  set(value) {
    modelStorage.value = value
  },
})

const activeParams = computed(() => getActiveFilters(storage.value, fields))

const count = computed(() =>
  activeParams.value?.params.reduce(
    (acc, group) => acc + group.params.length,
    0,
  ),
)

const handleToggle = () => {
  shown.value = !shown.value
}

const handleClear = () => {
  modelStorage.value = undefined
}

const applyModel = (value?: Filter) => {
  modelValue.value = flat ? formatFilterParams(value) : value
}

watch(
  activeParams,
  (value, prev) => {
    if (isEqual(value, prev)) return
    applyModel(value)
  },
  { immediate: true },
)

onClickOutside(containerRef, () => {
  if (!shown.value) return
  shown.value = false
})

useHotKeys(
  'escape',
  e => {
    e?.stopPropagation()
    if (!shown.value) return
    shown.value = false
    return true
  },
  { priority: 90 },
)
</script>

<style>
.ui-filter {
  @apply p-4 pt-2;

  &__header {
    @apply flex items-center;
    @apply mb-3.5 gap-0.5;
  }

  &__header-title {
    @apply flex-auto shrink-0;
    @apply text-base sm:text-sm;
    @apply text-gray-500 dark:text-gray-300;
  }

  &__header-button {
    @apply ml-1 sm:mx-1.5;
  }
}
</style>
